import React, { useEffect, useState } from 'react';
import { opsApi, type OpsSettings } from '../../../api/dbOps';
import { errorText } from '../../../api/migrationRunner';
import { Alert, Btn, ErrorBox, Panel, Pill, Spinner, when } from '../runner/ui';
import { input, sel, td, th, usePoll } from './shared';

/**
 * Alerts (approved mockup "Database redesign", Alerts): "Tell me when" —
 * checked by the scheduler, delivered to the super-admin bell (System
 * Monitoring › Alerts) and by email.
 */
const AlertsTab: React.FC<{ isOwner: boolean }> = ({ isOwner }) => {
  const settingsQ = usePoll(() => opsApi.settings(), null);
  const alertsQ = usePoll(() => opsApi.alerts(), 30000);
  const [s, setS] = useState<OpsSettings | null>(null);
  const [extra, setExtra] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'bad' | 'info'; text: string } | null>(null);
  useEffect(() => {
    if (settingsQ.data) { setS(settingsQ.data.settings); setExtra(settingsQ.data.settings.alerts.extra_emails.join(', ')); }
  }, [settingsQ.data]);

  const ro = !isOwner;
  const setA = <K extends keyof OpsSettings['alerts']>(k: K, v: OpsSettings['alerts'][K]) => s && setS({ ...s, alerts: { ...s.alerts, [k]: v } });

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const next = await opsApi.saveSettings({ ...s, alerts: { ...s.alerts, extra_emails: extra.split(/[\s,;]+/).filter(Boolean) } });
      setS(next);
      setExtra(next.alerts.extra_emails.join(', '));
      setMsg({ tone: 'ok', text: 'Saved.' });
    } catch (err) {
      setMsg({ tone: 'bad', text: errorText(err) });
    } finally {
      setSaving(false);
    }
  };
  const checkNow = async () => {
    try {
      const r = await opsApi.checkAlerts();
      setMsg({ tone: 'info', text: `Checked: ${r.raised} new, ${r.repeated} still open, ${r.resolved} cleared.` });
      void alertsQ.reload();
    } catch (err) {
      setMsg({ tone: 'bad', text: errorText(err) });
    }
  };
  const resolve = async (id: string) => {
    try { await opsApi.resolveAlert(id); void alertsQ.reload(); } catch (err) { setMsg({ tone: 'bad', text: errorText(err) }); }
  };

  const row = (label: string, hint: string | null, control: React.ReactNode) => (
    <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 py-3 last:border-0">
      <div className="min-w-[220px] flex-1 text-[13.5px] text-slate-800">{label}{hint && <small className="block text-[12px] text-slate-500">{hint}</small>}</div>
      {control}
    </div>
  );
  const onOff = (v: boolean, set: (b: boolean) => void, onLabel = 'On') => (
    <select className={sel} disabled={ro} value={v ? 'on' : 'off'} onChange={(e) => set(e.target.value === 'on')}>
      <option value="on">{onLabel}</option><option value="off">Off</option>
    </select>
  );

  const open = (alertsQ.data?.alerts || []).filter((a) => !a.is_resolved);
  const recent = (alertsQ.data?.alerts || []).filter((a) => a.is_resolved).slice(0, 10);

  return (
    <div className="space-y-4">
      {msg && <Alert tone={msg.tone} title={msg.text} action={<Btn kind="ghost" small onClick={() => setMsg(null)}>Dismiss</Btn>} />}
      {settingsQ.error ? <ErrorBox>{errorText(settingsQ.error)}</ErrorBox> : null}
      {!s ? <Spinner /> : (
        <Panel title="Tell me when" actions={isOwner ? <Btn small disabled={saving} onClick={save}>{saving && <Spinner />}Save</Btn> : <Pill tone="mut">owners can change these</Pill>}>
          {row("A server's disk passes", 'Tenant and registry servers (when the server reports its disk).', (
            <select className={sel} disabled={ro} value={s.alerts.disk_percent} onChange={(e) => setA('disk_percent', Number(e.target.value))}>
              {[70, 75, 80, 85, 90, 95].map((n) => <option key={n} value={n}>{n}%</option>)}
            </select>
          ))}
          {row('A table stays locked longer than', null, (
            <select className={sel} disabled={ro} value={s.alerts.lock_minutes} onChange={(e) => setA('lock_minutes', Number(e.target.value))}>
              {[1, 2, 5, 10, 15, 30].map((n) => <option key={n} value={n}>{n} min</option>)}
            </select>
          ))}
          {row("A backup fails or can't be verified", null, onOff(s.alerts.backup_failed, (v) => setA('backup_failed', v)))}
          {row('A company drifts from the template', null, onOff(s.alerts.drift_daily, (v) => setA('drift_daily', v), 'Daily check'))}
          {row('A migration batch fails', 'Batch emails follow Settings › "Tell me when a batch ends".', onOff(s.alerts.batch_failed, (v) => setA('batch_failed', v)))}
          {row('Send to', 'Super-admin bell and email.', (
            <select className={sel} disabled={ro} value={s.alerts.recipients} onChange={(e) => setA('recipients', e.target.value as 'owners' | 'all')}>
              <option value="owners">Owners</option><option value="all">Every operator</option>
            </select>
          ))}
          {row('Also email', 'Comma-separated.', (
            <input className={`${input} max-w-sm`} disabled={ro} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="ops@example.com" />
          ))}
        </Panel>
      )}

      <Panel title="Open database alerts" bodyClass="p-0" actions={isOwner ? <Btn kind="ghost" small onClick={checkNow}>Check now</Btn> : undefined}>
        {open.length === 0 ? <div className="p-4 text-[13px] text-slate-500">{alertsQ.loading ? 'Loading…' : 'Nothing open.'}</div> : (
          <table className="w-full">
            <thead className="bg-slate-50"><tr><th className={th}>Since</th><th className={th}>Alert</th><th className={th}>Seen</th><th className={th} /></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {open.map((a) => (
                <tr key={a.id}>
                  <td className={`${td} whitespace-nowrap`}>{when(a.triggered_at)}</td>
                  <td className={td}>
                    <Pill tone={a.severity === 'critical' ? 'bad' : a.severity === 'warning' ? 'warn' : 'info'}>{a.alert_name.replace('Database: ', '')}</Pill>
                    <div className="mt-1">{a.alert_message}</div>
                  </td>
                  <td className={`${td} whitespace-nowrap`}>{a.occurrence_count > 1 ? `${a.occurrence_count} times, last ${when(a.last_seen_at)}` : 'once'}</td>
                  <td className={`${td} text-right`}>{isOwner && <Btn kind="ghost" small onClick={() => resolve(a.id)}>Resolve</Btn>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
      {recent.length > 0 && (
        <details className="text-[13px] text-slate-600">
          <summary className="cursor-pointer">Recently resolved</summary>
          <ul className="mt-2 space-y-1">
            {recent.map((a) => <li key={a.id}>{when(a.resolved_at)} · {a.alert_message} <span className="text-slate-400">({a.resolved_by})</span></li>)}
          </ul>
        </details>
      )}
    </div>
  );
};

export default AlertsTab;
